/**
 * "En son hangi görünümdeydi" tercihi (web: OctoViewMode çerezi).
 *
 * Kullanıcı el terminali görünümüne geçip uygulamayı kapattığında, tekrar açılışta
 * masaüstü kabuğuyla karşılaşmamalı — PWA kısayolu her zaman kök adresi açıyor.
 * Web bunu çerezde tutuyor ve kökü karşılayan sunucu isteğinde yönlendiriyordu;
 * React tek sayfa olduğu için karar istemcide veriliyor, saklama yeri tarayıcı.
 *
 * Veritabanındaki `PrefersNodeView`ten FARKLI bir şey: o "girişte hangi görünüm
 * açılsın" diye bilinçli yapılan, her cihazda geçerli bir seçim; bu ise cihaza özel
 * "en son neredeydin" hafızası.
 */
export type OctoViewMode = 'node' | 'web';

const KEY = 'octo.view';

/** Tercihi okur; yazılamayan/okunamayan tarayıcıda (gizli sekme, kapalı depolama) 'web'. */
export function lastViewMode(): OctoViewMode {
  return storedViewMode() ?? 'web';
}

/** Cihazda yazılı tercih; hiç yazılmamışsa null (lastViewMode bunu 'web' sayar). */
export function storedViewMode(): OctoViewMode | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'node' || v === 'web' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Kök adres ("/") açılışında node görünümüne mi gidilecek? (OCTO_CORE_NEW#559)
 *
 * Önceden yalnız cihaz hafızasına bakılıyordu: `PrefersNodeView = 1` kullanıcı "Beni
 * hatırla" ile oturumu açık gelince, F5'te ya da PWA kısayoluyla kök adrese düşünce
 * LoginPage hiç çalışmadığı için masaüstü kabuğu açılıyordu. Kural:
 *  - cihazda yazılı tercih varsa o geçerli ('node' → node, 'web' → masaüstü: kullanıcı
 *    node'dan bilinçli olarak masaüstüne geçmiş, geri gönderilmemeli);
 *  - yazılı tercih yoksa kullanıcının profili (yalnız node lisansı ya da PrefersNodeView).
 * Derin linkler (/s/{id}) buradan geçmez.
 */
export function rootOpensNodeView(user: Parameters<typeof opensNodeView>[0]): boolean {
  const stored = storedViewMode();
  if (stored) return stored === 'node';
  return opensNodeView(user);
}

export function rememberViewMode(mode: OctoViewMode): void {
  try {
    localStorage.setItem(KEY, mode);
  } catch {
    // Hatırlanamazsa tek kayıp bir sonraki açılışın masaüstünden başlaması; akış kesilmez.
  }
}

/**
 * Giriş sonrası (ya da kök adres açılışında) node görünümüne mi düşülecek?
 *
 * Web'in kuralı: yalnızca node lisansı olan kullanıcının masaüstünde işi yok; "girişte
 * node görünümü" diyen de oraya düşer. Mobil tarayıcı tek başına bir sebep DEĞİL —
 * Octo Web'in kendi mobil düzeni var, el terminali akışını isteyen bunu seçiyor.
 */
export function opensNodeView(user: { hasWebLicense?: boolean; hasNodeLicense?: boolean; prefersNodeView?: boolean } | null | undefined): boolean {
  if (!user) return false;
  const nodeOnly = !!user.hasNodeLicense && !user.hasWebLicense;
  return nodeOnly || !!user.prefersNodeView;
}

/**
 * Masaüstünden node görünümüne geçiş.
 *
 * **Web yetkisi olan kullanıcıda node AYRI SEKMEDE açılır:** o kullanıcı node'a
 * bakarken masaüstündeki işini (açık sekmeler, yarım kalmış form) kaybetmemeli. Sekme
 * adlandırılmış — ikinci kez basıldığında yeni bir sekme daha açmak yerine aynısına
 * dönülür.
 *
 * Bu bir "görünüm değiştirme" değil "yanında açma" olduğu için cihazın son-görünüm
 * hafızası YAZILMAZ: kullanıcı hâlâ web'de. Yalnızca node lisanslı kullanıcı (ya da
 * açılır pencere engellenmişse herkes) aynı sekmede geçer, orada hafıza yazılır.
 *
 * @returns Açılış biçimi; çağıran aynı sekmede gezinmeyi buna göre yapar.
 */
export function openNodeView(opts: { hasWebLicense?: boolean }, navigateSameTab: () => void): 'newTab' | 'sameTab' {
  if (opts.hasWebLicense) {
    const win = typeof window !== 'undefined' ? window.open('/node', 'octo-node') : null;
    if (win) {
      win.focus();
      return 'newTab';
    }
    // Açılır pencere engellendi: kullanıcı bir yere gitmeli, aynı sekmede açılır.
  }
  rememberViewMode('node');
  navigateSameTab();
  return 'sameTab';
}
